import chmln from "@chamaeleonidae/chmln";
import { Aurelia } from "aurelia-framework";
import { SoramaUser } from "datatypes/SoramaWebApi";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";
import { RepresentativeRepository } from "repositories/RepresentativeRepository";

const CHMLNObservableScope = "ObservableChameleon";
const CHMLNEventScope = "ChangedChameleon";
const CHMLNListenerExistscope = "ListenerExistsChameleon";

/**
 * Integration with Chameleon to hide an element (most cases next button)
 *
 * EG:
 *
 * // check if you can continue tour, always with a developer
 * chameleon.addButtonVisibilityObserver('#chmln-button-63d282b23f513400176dbcb4', 'camera')
 */

export class Chameleon {
    public static initialize() {
        chmln.init("S6axdefe6Rqxjw0tOS8n8IKWOrpJQ6F8FFArPizbPHBhss-1OnV9Y-DQ69LLp0iwrKiHRo", {
            fastUrl: "https://fast.chameleon.io/",
        });
    }

    public static setAuthentication(aurelia: Aurelia) {
        const chameleonAuthenticationHandler = <IAuthenticationHandler>{
            onSignIn: (userId: string) => {
                aurelia.container
                    .get(RepresentativeRepository)
                    .getRepresentative(userId)
                    .then((representative) => {
                        const user = representative as unknown as SoramaUser;
                        /* chameleon.io user identification and data */

                        chmln.identify(user.Id); // REQUIRED, the unique ID of each user in your database (e.g. 23443 or "690b80e5f433ea81b96c9bf6"
                    });
            },
        };
        aurelia.container.get(IAuthentication).addAuthenticationHandler(chameleonAuthenticationHandler);
    }

    public static setObservable(key: string, value: boolean) {
        (window as typeof globalThis).chameleon[key + CHMLNObservableScope] = value;
    }

    public static getObservable(key: string): boolean {
        return (window as typeof globalThis).chameleon[key + CHMLNObservableScope];
    }

    public static listenerExists(key: string): boolean {
        return (window as typeof globalThis).chameleon[key + CHMLNListenerExistscope];
    }

    public static addEventListener(key: string, callback: () => void) {
        addEventListener(key + CHMLNEventScope, callback);
        (window as typeof globalThis).chameleon[key + CHMLNListenerExistscope] = true;
    }

    public static checkObservable(key: string) {
        dispatchEvent(new Event(key + CHMLNEventScope));
    }
}

(window as typeof globalThis).chameleon = {};

// Based on certain status events throughout the portal code (e.g. wait for hardware to be connected),
// a chameleon button's visibility is changed
(window as typeof globalThis).chameleon.addButtonVisibilityObserver = (selector, key) => {
    if (!Chameleon.listenerExists(key)) {
        Chameleon.addEventListener(key, () => applyVisibilityStyling(selector, key));

        applyVisibilityStyling(selector, key);

        const observer = new MutationObserver(() => applyVisibilityStyling(selector, key));
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
};

function applyVisibilityStyling(selector: keyof HTMLElementTagNameMap, key: string) {
    if (!document.querySelector(selector)) return;

    if (Chameleon.getObservable(key)) {
        document.querySelector(selector).style.display = "block";
    } else {
        document.querySelector(selector).style.display = "none";
    }
}
